import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { TEST_IDS } from "../../constants/testIds";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { utcToLocalInput, localInputToUtc, formatInTz } from "../../lib/dates";
import { Trash2, Plus, Send, Repeat, GripVertical, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import AttachmentsSection from "./AttachmentsSection";
import ProjectFolderFiles from "./ProjectFolderFiles";
import LabelMultiSelect from "./LabelMultiSelect";
import CommentReactions from "./CommentReactions";
import MentionTextarea from "./MentionTextarea";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";

function newSubtask(title = "") {
  return { id: Math.random().toString(36).slice(2, 10), title, done: false };
}

export default function TaskDialog({ task, users, onClose, onSave, onDelete, tenantTz }) {
  const { t } = useTranslation();
  const { user: me, tenant } = useAuthStore();
  const [form, setForm] = useState({
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    assignee_id: null,
    due_at: "",
    recurrence: "none",
    label_ids: [],
  });
  const [subtasks, setSubtasks] = useState([]);
  const [newSub, setNewSub] = useState("");
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [tab, setTab] = useState("details");
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimerRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        status: task.status,
        priority: task.priority,
        assignee_id: task.assignee_id,
        due_at: task.due_at ? utcToLocalInput(task.due_at, tenantTz || "UTC") : "",
        recurrence: task.recurrence || "none",
        label_ids: task.label_ids || [],
      });
      setSubtasks(task.subtasks || []);
      setTab("details");
      loadComments();
      loadActivity();
      // Auto mark-read
      api.post("/users/me/mark-read", { task_id: task.id }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  // WS listener for reaction / comment updates + typing while dialog is open
  useTenantWebSocket((msg) => {
    if (!msg?.event || !task) return;
    if (msg.task_id && msg.task_id !== task.id) return;
    if (msg.event === "reaction.updated") {
      setComments((cs) =>
        cs.map((c) =>
          c.id === msg.comment_id ? { ...c, reactions: msg.reactions } : c,
        ),
      );
    } else if (msg.event === "comment.created") {
      loadComments();
      loadActivity();
    } else if (msg.event === "typing") {
      if (!msg.user_id || msg.user_id === me?.id) return;
      setTypingUsers((cur) => {
        const next = { ...cur };
        if (msg.is_typing) next[msg.user_id] = Date.now();
        else delete next[msg.user_id];
        return next;
      });
    }
  });

  // Prune stale typing indicators (>5s)
  useEffect(() => {
    const int = setInterval(() => {
      const now = Date.now();
      setTypingUsers((cur) => {
        let changed = false;
        const next = {};
        for (const [k, ts] of Object.entries(cur)) {
          if (now - ts < 5000) next[k] = ts;
          else changed = true;
        }
        return changed ? next : cur;
      });
    }, 2000);
    return () => clearInterval(int);
  }, []);

  const emitTyping = (is_typing) => {
    const ws = window.__taskoraWs;
    if (!ws || ws.readyState !== 1 || !task) return;
    try {
      ws.send(
        JSON.stringify({ event: "typing", task_id: task.id, is_typing }),
      );
    } catch {
      /* ignore */
    }
  };

  const handleCommentChange = (val) => {
    setNewComment(val);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (val.length > 0) {
      emitTyping(true);
      typingTimerRef.current = setTimeout(() => emitTyping(false), 2500);
    } else {
      emitTyping(false);
    }
  };

  const loadComments = async () => {
    if (!task) return;
    const { data } = await api.get(`/tasks/${task.id}/comments`);
    setComments(data);
  };
  const loadActivity = async () => {
    if (!task) return;
    const { data } = await api.get(`/tasks/${task.id}/activity`);
    setActivity(data);
  };

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  const submit = async (e) => {
    e.preventDefault();
    const patch = {
      title: form.title,
      description: form.description,
      status: form.status,
      priority: form.priority,
      assignee_id: form.assignee_id,
      due_at: form.due_at ? localInputToUtc(form.due_at, tenantTz || "UTC") : null,
      recurrence: form.recurrence,
      label_ids: form.label_ids,
    };
    await onSave(patch);
    // Persist subtasks separately
    try {
      await api.patch(`/tasks/${task.id}/subtasks`, { subtasks });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    onClose();
  };

  const addSubtask = () => {
    if (!newSub.trim()) return;
    setSubtasks((s) => [...s, newSubtask(newSub.trim())]);
    setNewSub("");
  };

  const toggleSub = (id) => {
    setSubtasks((s) => s.map((x) => (x.id === id ? { ...x, done: !x.done } : x)));
  };

  const removeSub = (id) => {
    setSubtasks((s) => s.filter((x) => x.id !== id));
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await api.post(`/tasks/${task.id}/comments`, { body: newComment.trim() });
      setNewComment("");
      loadComments();
      loadActivity();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/tasks/${task.id}/comments/${cid}`);
      loadComments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const subtaskProgress = subtasks.length
    ? Math.round(
        (subtasks.filter((s) => s.done).length / subtasks.length) * 100,
      )
    : 0;

  return (
    <Dialog open={!!task} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {task?.title || "Task"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Edit task with subtasks, comments and activity log.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-zinc-100" data-testid={TEST_IDS.taskTabs}>
            <TabsTrigger value="details" data-testid={TEST_IDS.taskTabDetails}>
              Details
            </TabsTrigger>
            <TabsTrigger value="subtasks" data-testid={TEST_IDS.taskTabSubtasks}>
              Subtasks ({subtasks.length})
            </TabsTrigger>
            <TabsTrigger value="comments" data-testid={TEST_IDS.taskTabComments}>
              Comments ({comments.length})
            </TabsTrigger>
            <TabsTrigger value="files" data-testid={TEST_IDS.taskTabFiles}>
              Files
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid={TEST_IDS.taskTabActivity}>
              Activity
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto pt-4">
            <TabsContent value="details" className="mt-0 space-y-4">
              <form id="task-details-form" onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    {t("tasks.title")}
                  </Label>
                  <Input
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid={TEST_IDS.taskDialogTitle}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    {t("tasks.description")}
                  </Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      {t("tasks.status")}
                    </Label>
                    <Select
                      value={form.status}
                      onValueChange={(v) => setForm({ ...form, status: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">{t("tasks.status_todo")}</SelectItem>
                        <SelectItem value="in_progress">{t("tasks.status_in_progress")}</SelectItem>
                        <SelectItem value="done">{t("tasks.status_done")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      {t("tasks.priority")}
                    </Label>
                    <Select
                      value={form.priority}
                      onValueChange={(v) => setForm({ ...form, priority: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["low", "medium", "high", "urgent"].map((p) => (
                          <SelectItem key={p} value={p}>
                            {t(`tasks.priority_${p}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      {t("tasks.assignee")}
                    </Label>
                    <Select
                      value={form.assignee_id || "__none__"}
                      onValueChange={(v) =>
                        setForm({ ...form, assignee_id: v === "__none__" ? null : v })
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t("tasks.unassigned")}</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      {t("tasks.due_at")} ({tenantTz || "UTC"})
                    </Label>
                    <Input
                      type="datetime-local"
                      value={form.due_at}
                      onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                    <Repeat className="h-3 w-3" />
                    {t("tasks.recurrence")}
                  </Label>
                  <Select
                    value={form.recurrence}
                    onValueChange={(v) => setForm({ ...form, recurrence: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid={TEST_IDS.taskRecurrenceSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("tasks.recurrence_none")}</SelectItem>
                      <SelectItem value="daily">{t("tasks.recurrence_daily")}</SelectItem>
                      <SelectItem value="weekly">{t("tasks.recurrence_weekly")}</SelectItem>
                      <SelectItem value="monthly">{t("tasks.recurrence_monthly")}</SelectItem>
                      <SelectItem value="yearly">{t("tasks.recurrence_yearly")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.recurrence !== "none" && !form.due_at && (
                    <p className="mt-1 text-xs text-amber-700">
                      {t("tasks.recurrence_needs_due")}
                    </p>
                  )}
                </div>
                {/* Etiketler — çoklu seçim + inline yeni etiket oluşturma */}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                    <TagIcon className="h-3 w-3" />
                    Etiketler
                  </Label>
                  <div className="mt-1">
                    <LabelMultiSelect
                      value={form.label_ids || []}
                      onChange={(next) => setForm({ ...form, label_ids: next })}
                      testIdPrefix="task-dialog-labels"
                    />
                  </div>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="subtasks" className="mt-0 space-y-3">
              {subtasks.length > 0 && (
                <div className="mb-4">
                  <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
                    <span>{subtaskProgress}% complete</span>
                    <span>
                      {subtasks.filter((s) => s.done).length}/{subtasks.length}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                    <div
                      className="h-full bg-zinc-900 transition-all duration-300"
                      style={{ width: `${subtaskProgress}%` }}
                    />
                  </div>
                </div>
              )}
              <ul className="space-y-2">
                {subtasks.map((s, idx) => (
                  <li
                    key={s.id}
                    data-testid={TEST_IDS.subtaskRow(s.id)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", String(idx));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                      if (Number.isNaN(from) || from === idx) return;
                      setSubtasks((cur) => {
                        const next = [...cur];
                        const [moved] = next.splice(from, 1);
                        next.splice(idx, 0, moved);
                        return next;
                      });
                    }}
                    className="group flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 cursor-move"
                  >
                    <GripVertical
                      className="h-3 w-3 text-zinc-300"
                      data-testid={TEST_IDS.subtaskDragHandle(s.id)}
                    />
                    <Checkbox
                      checked={s.done}
                      onCheckedChange={() => toggleSub(s.id)}
                      data-testid={TEST_IDS.subtaskCheckbox(s.id)}
                    />
                    <span
                      className={[
                        "flex-1 text-sm",
                        s.done ? "text-zinc-400 line-through" : "text-zinc-900",
                      ].join(" ")}
                    >
                      {s.title}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSub(s.id)}
                      className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
                {subtasks.length === 0 && (
                  <li className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-xs text-zinc-500">
                    {t("tasks.no_subtasks")}
                  </li>
                )}
              </ul>
              <div className="flex items-center gap-2">
                <Input
                  placeholder={t("tasks.add_subtask")}
                  value={newSub}
                  onChange={(e) => setNewSub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSubtask();
                    }
                  }}
                  data-testid={TEST_IDS.subtaskAddInput}
                />
                <Button
                  type="button"
                  onClick={addSubtask}
                  size="sm"
                  variant="outline"
                  className="gap-1 border-zinc-300"
                  data-testid={TEST_IDS.subtaskAddBtn}
                >
                  <Plus className="h-3 w-3" />
                  {t("common.add")}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="comments" className="mt-0 space-y-3">
              <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {comments.map((c) => (
                  <li key={c.id} className="rounded-lg border border-zinc-200 bg-white p-3">
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-medium text-zinc-900">
                        {userMap[c.user_id]?.name || "—"}
                      </span>
                      <span className="text-zinc-500">
                        {formatInTz(c.created_at, tenantTz, "DD MMM HH:mm")}
                      </span>
                    </div>
                    <div className="prose prose-sm prose-zinc max-w-none whitespace-pre-wrap text-sm text-zinc-700 [&_a]:text-blue-600 [&_code]:rounded [&_code]:bg-zinc-100 [&_code]:px-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {c.body}
                      </ReactMarkdown>
                    </div>
                    <CommentReactions
                      comment={c}
                      users={users}
                      onUpdated={(reactions) =>
                        setComments((cs) =>
                          cs.map((x) =>
                            x.id === c.id ? { ...x, reactions } : x,
                          ),
                        )
                      }
                    />
                    {c.user_id === me?.id && (
                      <button
                        type="button"
                        onClick={() => deleteComment(c.id)}
                        className="mt-1 text-[10px] uppercase tracking-wider text-zinc-400 hover:text-red-600"
                      >
                        Delete
                      </button>
                    )}
                  </li>
                ))}
                {comments.length === 0 && (
                  <li className="rounded-md border border-dashed border-zinc-200 px-3 py-8 text-center text-xs text-zinc-500">
                    {t("tasks.no_comments")}
                  </li>
                )}
              </ul>
              <form onSubmit={postComment} className="space-y-1">
                {Object.keys(typingUsers).length > 0 && (
                  <div
                    data-testid={TEST_IDS.typingIndicator}
                    className="text-[11px] italic text-zinc-500"
                  >
                    {Object.keys(typingUsers)
                      .map((id) => userMap[id]?.name || "Someone")
                      .join(", ")}{" "}
                    {t("tasks.typing")}
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MentionTextarea
                    value={newComment}
                    onChange={handleCommentChange}
                    users={users}
                    placeholder={t("tasks.add_comment")}
                    rows={2}
                    className="flex-1"
                    showToolbar
                    data-testid={TEST_IDS.commentInput}
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
                    data-testid={TEST_IDS.commentSubmitBtn}
                  >
                    <Send className="h-3 w-3" />
                    {t("common.send")}
                  </Button>
                </div>
                <p
                  data-testid={TEST_IDS.commentMarkdownHint}
                  className="text-[10px] text-zinc-500"
                >
                  {t("tasks.markdown_hint")}
                </p>
              </form>
            </TabsContent>

            <TabsContent value="files" className="mt-0">
              <AttachmentsSection taskId={task?.id} tenantTz={tenantTz} />
              <ProjectFolderFiles projectId={task?.project_id} />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <ul className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {activity.map((a, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 h-1.5 w-1.5 rounded-full bg-zinc-400 shrink-0" />
                    <div className="flex-1 text-sm">
                      <span className="font-medium text-zinc-900">
                        {userMap[a.user_id]?.name || "system"}
                      </span>{" "}
                      <span className="text-zinc-500">{a.action}</span>
                      {a.changes && (
                        <span className="text-zinc-500">
                          {" "}
                          · {a.changes.join(", ")}
                        </span>
                      )}
                      <div className="text-xs text-zinc-400">
                        {formatInTz(a.timestamp, tenantTz, "DD MMM HH:mm:ss")}
                      </div>
                    </div>
                  </li>
                ))}
                {activity.length === 0 && (
                  <li className="rounded-md border border-dashed border-zinc-200 px-3 py-8 text-center text-xs text-zinc-500">
                    {t("tasks.no_activity")}
                  </li>
                )}
              </ul>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t border-zinc-100 pt-4 flex justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={onDelete}
            className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            {t("tasks.delete")}
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid={TEST_IDS.taskDialogClose}
            >
              {t("common.close")}
            </Button>
            <Button
              type="submit"
              form="task-details-form"
              data-testid={TEST_IDS.taskDialogSave}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {t("tasks.save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
